import { BoxDetection } from '@models';

export const isCenterInCircle = (check: {
  detection: BoxDetection;
  target: {
    x: number;
    y: number;
    radius: number;
  };
  confidence: number;
}): boolean => {
  if (check.detection) {
    const distance = Math.sqrt(
      (check.detection.center.x - check.target.x) ** 2 +
        (check.detection.center.y - check.target.y) ** 2,
    );
    return (
      check.detection.confidence > check.confidence &&
      distance <= check.target.radius
    );
  } else return null;
};

export const isCenterInBox = (check: {
  center: {
    x: number;
    y: number;
  };
  box:
    | BoxDetection
    | { min: { x: number; y: number }; max: { x: number; y: number } };
  threshold?: number;
}): boolean => {
  const threshold = check.threshold || 30;
  return (
    check.center.x > check.box.min.x - threshold &&
    check.center.y > check.box.min.y - threshold &&
    check.center.x < check.box.max.x + threshold &&
    check.center.y < check.box.max.y + threshold
  );
};

export const isBoxInBox = (check: {
  detection:
    | BoxDetection
    | { min: { x: number; y: number }; max: { x: number; y: number } };
  target: { min: { x: number; y: number }; max: { x: number; y: number } };
  confidence?: number;
}): boolean => {
  if (check.detection) {
    const confidence = (check.detection as BoxDetection)?.confidence ?? 1;

    return (
      check.detection.min.x >= check.target.min.x &&
      check.detection.min.y >= check.target.min.y &&
      check.detection.max.x <= check.target.max.x &&
      check.detection.max.y <= check.target.max.y &&
      confidence > (check.confidence ?? 0)
    );
  } else return null;
};

export const areBoxesOverlapping = (
  box1: {
    min: { x: number; y: number };
    max: { x: number; y: number };
  },
  box2: {
    min: { x: number; y: number };
    max: { x: number; y: number };
  },
): boolean => {
  return !(
    box1.max.x < box2.min.x ||
    box1.min.x > box2.max.x ||
    box1.max.y < box2.min.y ||
    box1.min.y > box2.max.y
  );
};

export const calculateIntersection = (
  rect1: BoxDetection,
  rect2: BoxDetection,
): number => {
  const xMin = Math.max(rect1.min.x, rect2.min.x);
  const yMin = Math.max(rect1.min.y, rect2.min.y);
  const xMax = Math.min(rect1.max.x, rect2.max.x);
  const yMax = Math.min(rect1.max.y, rect2.max.y);

  const intersectionArea =
    xMin <= xMax && yMin <= yMax ? (xMax - xMin) * (yMax - yMin) : 0;

  const unionArea =
    (rect1.max.x - rect1.min.x) * (rect1.max.y - rect1.min.y) +
    (rect2.max.x - rect2.min.x) * (rect2.max.y - rect2.min.y) -
    intersectionArea;

  return intersectionArea / unionArea;
};

export const calculateBoxIntersection = (
  box: BoxDetection,
  intersectionBox: BoxDetection,
): number => {
  const xMin = Math.max(box.min.x, intersectionBox.min.x);
  const yMin = Math.max(box.min.y, intersectionBox.min.y);
  const xMax = Math.min(box.max.x, intersectionBox.max.x);
  const yMax = Math.min(box.max.y, intersectionBox.max.y);

  const intersectionArea =
    xMin <= xMax && yMin <= yMax ? (xMax - xMin) * (yMax - yMin) : 0;

  const rectangleArea = (box.max.y - box.min.y) * (box.max.x - box.min.x);

  return intersectionArea / rectangleArea;
};

export const addTargetTolerance = (
  target: {
    min: {
      x: number;
      y: number;
    };
    max: {
      x: number;
      y: number;
    };
  },
  percentageX: number,
  percentageY: number,
) => {
  const xValue = ((target.max.x - target.min.x) * percentageX) / 100;
  const yValue = ((target.max.y - target.min.y) * percentageY) / 100;
  return {
    min: {
      x: target.min.x - xValue,
      y: target.min.y - yValue,
    },
    max: {
      x: target.max.x + xValue,
      y: target.max.y + yValue,
    },
  };
};
